import {
  CircleAlert,
  CircleCheck,
  CircleX,
  LoaderCircle,
  Map,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { CrawlJob, WithResourceId } from '../../../types/knowledge';
import { CrawlJobsGroup } from '../CrawlJobsGroup';

type KnowledgeCrawlsSectionsProps = {
  filteredCrawlsMapping: Array<WithResourceId<CrawlJob>>;
  filteredCrawlsNeedingAttention: Array<WithResourceId<CrawlJob>>;
  filteredCrawlsInProgress: Array<WithResourceId<CrawlJob>>;
  filteredCrawlsCompleted: Array<WithResourceId<CrawlJob>>;
  filteredCrawlsFailed: Array<WithResourceId<CrawlJob>>;
  isMappingExpanded: boolean;
  isNeedsAttentionExpanded: boolean;
  isInProgressExpanded: boolean;
  isCompletedExpanded: boolean;
  isFailedExpanded: boolean;
  translate: (key: string, params?: Record<string, string>) => string;
  onToggleMapping: () => void;
  onToggleNeedsAttention: () => void;
  onToggleInProgress: () => void;
  onToggleCompleted: () => void;
  onToggleFailed: () => void;
  renderCrawlListItem: (crawl: WithResourceId<CrawlJob>) => ReactNode;
};

export function KnowledgeCrawlsSections({
  filteredCrawlsMapping,
  filteredCrawlsNeedingAttention,
  filteredCrawlsInProgress,
  filteredCrawlsCompleted,
  filteredCrawlsFailed,
  isMappingExpanded,
  isNeedsAttentionExpanded,
  isInProgressExpanded,
  isCompletedExpanded,
  isFailedExpanded,
  translate,
  onToggleMapping,
  onToggleNeedsAttention,
  onToggleInProgress,
  onToggleCompleted,
  onToggleFailed,
  renderCrawlListItem,
}: KnowledgeCrawlsSectionsProps) {
  return (
    <div className="knowledge-database__crawl-sections">
      <CrawlJobsGroup
        id="mapping"
        title={translate('knowledge.crawlsMapping')}
        count={filteredCrawlsMapping.length}
        icon={Map}
        accent="mapping"
        isExpanded={isMappingExpanded}
        onToggle={onToggleMapping}
        expandLabel={translate('knowledge.crawlsMappingExpand')}
        collapseLabel={translate('knowledge.crawlsMappingCollapse')}
        emptyMessage={translate('knowledge.crawlsMappingEmpty')}
      >
        <ul className="knowledge-database__resource-list">
          {filteredCrawlsMapping.map(renderCrawlListItem)}
        </ul>
      </CrawlJobsGroup>
      <CrawlJobsGroup
        id="needs-attention"
        title={translate('knowledge.crawlsNeedsAttention')}
        count={filteredCrawlsNeedingAttention.length}
        icon={CircleAlert}
        accent="attention"
        isExpanded={isNeedsAttentionExpanded}
        onToggle={onToggleNeedsAttention}
        expandLabel={translate('knowledge.crawlsNeedsAttentionExpand')}
        collapseLabel={translate('knowledge.crawlsNeedsAttentionCollapse')}
        emptyMessage={translate('knowledge.crawlsNeedsAttentionEmpty')}
      >
        <ul className="knowledge-database__resource-list">
          {filteredCrawlsNeedingAttention.map(renderCrawlListItem)}
        </ul>
      </CrawlJobsGroup>
      <CrawlJobsGroup
        id="in-progress"
        title={translate('knowledge.crawlsInProgress')}
        count={filteredCrawlsInProgress.length}
        icon={LoaderCircle}
        accent="progress"
        isExpanded={isInProgressExpanded}
        onToggle={onToggleInProgress}
        expandLabel={translate('knowledge.crawlsInProgressExpand')}
        collapseLabel={translate('knowledge.crawlsInProgressCollapse')}
        emptyMessage={translate('knowledge.crawlsInProgressEmpty')}
      >
        <ul className="knowledge-database__resource-list">
          {filteredCrawlsInProgress.map(renderCrawlListItem)}
        </ul>
      </CrawlJobsGroup>
      <CrawlJobsGroup
        id="completed"
        title={translate('knowledge.crawlsCompleted')}
        count={filteredCrawlsCompleted.length}
        icon={CircleCheck}
        accent="completed"
        isExpanded={isCompletedExpanded}
        onToggle={onToggleCompleted}
        expandLabel={translate('knowledge.crawlsCompletedExpand')}
        collapseLabel={translate('knowledge.crawlsCompletedCollapse')}
        emptyMessage={translate('knowledge.crawlsCompletedEmpty')}
      >
        <ul className="knowledge-database__resource-list">
          {filteredCrawlsCompleted.map(renderCrawlListItem)}
        </ul>
      </CrawlJobsGroup>
      <CrawlJobsGroup
        id="failed"
        title={translate('knowledge.crawlsFailed')}
        count={filteredCrawlsFailed.length}
        icon={CircleX}
        accent="failed"
        isExpanded={isFailedExpanded}
        onToggle={onToggleFailed}
        expandLabel={translate('knowledge.crawlsFailedExpand')}
        collapseLabel={translate('knowledge.crawlsFailedCollapse')}
        emptyMessage={translate('knowledge.crawlsFailedEmpty')}
      >
        <ul className="knowledge-database__resource-list">
          {filteredCrawlsFailed.map(renderCrawlListItem)}
        </ul>
      </CrawlJobsGroup>
    </div>
  );
}
